import { cn } from "../../utils/common/cn";
import { LucideIcon, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import React from "react";

interface InteractiveCardProps {
  label: string;
  value: string | number;
  subValue?: string;
  icon?: LucideIcon;
  trend?: "up" | "down" | "neutral";
  trendValue?: string;
  variant?: "default" | "highlight" | "warning" | "success";
  className?: string;
  linkTo?: string;
  linkLabel?: string;
  onClick?: () => void;
}

export function InteractiveCard({
  label,
  value,
  subValue,
  icon: Icon,
  trend,
  trendValue,
  variant = "default",
  className,
  linkTo,
  linkLabel,
  onClick,
}: InteractiveCardProps) {
  const navigate = useNavigate();
  const isClickable = linkTo || onClick;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (linkTo) {
      navigate(linkTo);
    }
  };

  return (
    <div
      onClick={isClickable ? handleClick : undefined}
      className={cn(
        "metric-card group relative",
        variant === "highlight" && "border-primary/50 bg-primary/5",
        variant === "warning" && "border-warning/50 bg-warning/5",
        variant === "success" && "border-success/50 bg-success/5",
        isClickable && "cursor-pointer transition-all hover:border-primary hover:bg-primary/10 hover:scale-[1.02]",
        className
      )}
    >
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="metric-label">{label}</p>
          <p className={cn(
            "metric-value",
            variant === "warning" && "text-warning",
            variant === "success" && "text-success"
          )}>
            {value}
          </p>
          {subValue && (
            <p className="text-sm text-muted-foreground">{subValue}</p>
          )}
        </div>
        {Icon && (
          <div className={cn(
            "flex h-10 w-10 items-center justify-center rounded-lg transition-colors",
            variant === "highlight" && "bg-primary/10 group-hover:bg-primary/20",
            variant === "warning" && "bg-warning/10 group-hover:bg-warning/20",
            variant === "success" && "bg-success/10 group-hover:bg-success/20",
            variant === "default" && "bg-muted group-hover:bg-muted/80"
          )}>
            <Icon className={cn(
              "h-5 w-5",
              variant === "highlight" && "text-primary",
              variant === "warning" && "text-warning",
              variant === "success" && "text-success",
              variant === "default" && "text-muted-foreground"
            )} />
          </div>
        )}
      </div>
      {trend && trendValue && (
        <div className="mt-3 flex items-center gap-1 text-sm">
          <span
            className={cn(
              trend === "up" && "text-success",
              trend === "down" && "text-destructive",
              trend === "neutral" && "text-muted-foreground"
            )}
          >
            {trend === "up" ? "↑" : trend === "down" ? "↓" : "→"} {trendValue}
          </span>
        </div>
      )}
      {isClickable && (
        <div className="absolute bottom-3 right-3 flex items-center gap-1 text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity">
          <span>{linkLabel || "View details"}</span>
          <ChevronRight className="h-3 w-3" />
        </div>
      )}
    </div>
  );
}
